import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CourseResponse,
  DeleteCourseResponse,
  ListCoursesResponse,
  CreateCourseRequest,
  UpdateCourseRequest
} from '../models/course';
import { AssignedCourseItem, AssignedCoursesResponse, AssignCourseRequest, AssignCourseResponse } from '../models/course';

@Injectable({
  providedIn: 'root'
})
export class CoursesService {
  private readonly baseUrl = `${environment.BASE_URL}${environment.BASE_API.COURSE}`;
  private readonly listUrl = `${environment.BASE_URL}${environment.BASE_API.COURSE_LIST}`;
  private readonly assignedUrl = `${environment.BASE_URL}${environment.BASE_API.COURSE_ASSIGNED}`;
  private readonly assignUrl = `${this.baseUrl}/assign`;
  private readonly memberBaseUrl = `${environment.BASE_URL}wglearningbuddy/member`;

  constructor(private http: HttpClient) {}

  createCourse(payload: CreateCourseRequest): Observable<CourseResponse> {
    return this.http.post<CourseResponse>(this.baseUrl, payload);
  }

  listCourses(): Observable<ListCoursesResponse> {
    return this.http.get<ListCoursesResponse>(this.listUrl);
  }

  getCourse(courseId: string): Observable<CourseResponse> {
    return this.http.get<CourseResponse>(`${this.baseUrl}/${encodeURIComponent(courseId)}`);
  }

  updateCourse(courseId: string, payload: UpdateCourseRequest): Observable<CourseResponse> {
    return this.http.put<CourseResponse>(`${this.baseUrl}/${encodeURIComponent(courseId)}`, payload);
  }

  deleteCourse(courseId: string): Observable<DeleteCourseResponse> {
    return this.http.delete<DeleteCourseResponse>(`${this.baseUrl}/${encodeURIComponent(courseId)}`);
  }

  assignCourse(member: string, courseId: string): Observable<AssignCourseResponse> {
    const payload: AssignCourseRequest = {
      MEMBER: member,
      COURSE_ID: courseId
    };
    return this.http.post<AssignCourseResponse>(this.assignUrl, payload);
  }

  updateMemberCourseStatus(member: string, courseId: string, status: 'IN_PROGRESS' | 'COMPLETED') {
    const m = encodeURIComponent(member);
    const c = encodeURIComponent(courseId);
    const url = `${this.memberBaseUrl}/${m}/course/${c}/status`;
    return this.http.put<{ success: boolean; message: string; body: any }>(url, { STATUS: status });
  }

  getAssigned(): Observable<AssignedCoursesResponse> {
    return this.http.get<AssignedCoursesResponse>(this.assignedUrl);
  }

  getAssignedCourses(): Observable<AssignedCourseItem[]> {
    return this.getSpecificAssignedCourses();
  }

  private getSpecificAssignedCourses(): Observable<AssignedCourseItem[]> {
    return this.getAssigned().pipe(
      map((res) => {
        const body = Array.isArray((res as any)?.body) ? (res as any).body : [];

        // Old shape: [{ COURSES: AssignedCourseItem[] }, ...]
        if (body.some((item: any) => Array.isArray(item?.COURSES))) {
          const courses = body.flatMap((item: any) => Array.isArray(item?.COURSES) ? item.COURSES : []);
          return courses as AssignedCourseItem[];
        }

        // New shape: flat array with COURSE_ID, STATUS, ASSIGNED_ON, STARTED_ON, COMPLETED_ON
        const mapped = body
          .filter((row: any) => row && row.COURSE_ID && row.STATUS)
          .map((row: any) => ({
            COURSE_ID: String(row.COURSE_ID),
            STATUS: String(row.STATUS).toUpperCase() as AssignedCourseItem['STATUS'],
            ASSIGNED_ON: row.ASSIGNED_ON ?? undefined,
            STARTED_ON: row.STARTED_ON ?? undefined,
            COMPLETED_ON: row.COMPLETED_ON ?? undefined
          } as AssignedCourseItem));

        return mapped;
      })
    );
  }
}


